import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdBlock from '../components/AdBlock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import JobCard from '../components/JobCard';
import PaginationBar from '../components/PaginationBar';
import { toast } from 'sonner';
import {
  Search, MapPin, SlidersHorizontal, X, Briefcase, Filter,
  Bell, Loader2, Sparkles, CheckCircle2, FileText, ChevronDown
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

export default function JobSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const navigate = useNavigate();
  const { user, token, api, isAuthenticated, isJobSeeker } = useAuth();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Applied status map: job_id → {status, application_id}
  const [appliedMap, setAppliedMap] = useState({});

  // Saved/bookmarked job IDs
  const [savedJobs, setSavedJobs] = useState(new Set());

  // Quick-apply modal
  const [quickApplyJob, setQuickApplyJob] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [applying, setApplying] = useState(false);
  const [generatingCL, setGeneratingCL] = useState(false);
  const [appliedSuccess, setAppliedSuccess] = useState(null); // job that was just applied to

  // Save-as-alert modal
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertName, setAlertName] = useState('');
  const [alertFrequency, setAlertFrequency] = useState('daily');
  const [savingAlert, setSavingAlert] = useState(false);

  // Filter states
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [jobType, setJobType] = useState(searchParams.get('job_type') || '');
  const [remote, setRemote] = useState(searchParams.get('remote') === 'true');
  const [salaryMin, setSalaryMin] = useState(searchParams.get('salary_min') || '');
  const [salaryMax, setSalaryMax] = useState(searchParams.get('salary_max') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || '');
  const [country, setCountry] = useState(searchParams.get('country') || '');

  // Fetch available categories, subcategories and countries
  const [categories, setCategories] = useState([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetchJobs();
    fetchCategories();
    fetchSubcategories();
    fetchCountries();
  }, [searchParams]);

  // Sync alert name with current search term
  useEffect(() => {
    const kw = searchParams.get('search');
    const loc = searchParams.get('location');
    setAlertName([kw, loc].filter(Boolean).join(' in ') || 'All jobs');
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/categories`);
      setCategories(res.data.categories || []);
    } catch (error) {
      setCategories([]);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/jobs/subcategories`);
      const grouped = {};
      (res.data.subcategories || []).forEach(sub => {
        if (!grouped[sub.category_id]) grouped[sub.category_id] = [];
        grouped[sub.category_id].push(sub);
      });
      setSubcategoriesByCategory(grouped);
    } catch (error) {
      setSubcategoriesByCategory({});
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/countries`);
      setCountries(res.data.countries || []);
    } catch (error) {
      setCountries([]);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      ['search','location','job_type','salary_min','salary_max','category','subcategory','country','sector'].forEach(k => {
        if (searchParams.get(k)) params.set(k, searchParams.get(k));
      });
      if (searchParams.get('remote') === 'true') params.set('remote', 'true');

      // The count endpoint takes the filters only; the list also takes the page window.
      const listParams = new URLSearchParams(params);
      listParams.set('skip', String((page - 1) * PAGE_SIZE));
      listParams.set('limit', String(PAGE_SIZE));

      const [jobsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/jobs?${listParams.toString()}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} }),
        axios.get(`${API_URL}/api/jobs/count?${params.toString()}`)
      ]);
      setJobs(jobsRes.data);
      setTotalCount(countRes.data.count);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch applied status map when user is authenticated
  useEffect(() => {
    if (!isAuthenticated || !isJobSeeker) return;
    api.get('/applications/my-status')
      .then(res => setAppliedMap(res.data || {}))
      .catch(() => {});
  }, [isAuthenticated, isJobSeeker]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (location) params.set('location', location);
    if (jobType) params.set('job_type', jobType);
    if (category) params.set('category', category);
    if (subcategory) params.set('subcategory', subcategory);
    if (country) params.set('country', country);
    if (remote) params.set('remote', 'true');
    if (salaryMin) params.set('salary_min', salaryMin);
    if (salaryMax) params.set('salary_max', salaryMax);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch(''); setLocation(''); setJobType(''); setCategory(''); setSubcategory(''); setCountry('');
    setRemote(false); setSalaryMin(''); setSalaryMax('');
    setSearchParams(new URLSearchParams());
  };

  const goToPage = (p) => {
    const params = new URLSearchParams(searchParams);
    if (p > 1) params.set('page', String(p)); else params.delete('page');
    setSearchParams(params);
  };

  // A stale ?page=N past the last page (e.g. after the job count shrank) falls back to the last page.
  useEffect(() => {
    if (loading || page <= 1 || jobs.length > 0 || totalCount <= 0) return;
    goToPage(Math.max(1, Math.ceil(totalCount / PAGE_SIZE)));
  }, [loading, jobs.length, totalCount, page]);

  const selectCategory = (cat) => {
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    params.set('category', cat.slug || cat.name);
    params.delete('subcategory');
    setCategory(cat.slug || cat.name);
    setSubcategory('');
    setSearchParams(params);
  };

  const selectSubcategory = (cat, sub) => {
    const params = new URLSearchParams(searchParams);
    params.delete('page');
    params.set('category', cat.slug || cat.name);
    params.set('subcategory', sub.slug || sub.name);
    setCategory(cat.slug || cat.name);
    setSubcategory(sub.slug || sub.name);
    setSearchParams(params);
  };

  const clearCategoryFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('category'); params.delete('subcategory'); params.delete('page');
    setCategory(''); setSubcategory('');
    setSearchParams(params);
  };

  const openQuickApply = (job) => {
    if (!isAuthenticated) { navigate(`/login?redirect=/jobs`); return; }
    if (!isJobSeeker) { toast.error('Only job seekers can apply'); return; }
    setQuickApplyJob(job);
    setCoverLetter('');
    setAppliedSuccess(null);
  };

  const submitQuickApply = async () => {
    if (!quickApplyJob) return;
    setApplying(true);
    try {
      await axios.post(`${API_URL}/api/applications`, {
        job_id: quickApplyJob.id,
        cover_letter: coverLetter
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAppliedMap(prev => ({ ...prev, [quickApplyJob.id]: { status: 'pending', application_id: 'new' } }));
      setAppliedSuccess(quickApplyJob);
      toast.success('Application submitted!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const generateCoverLetter = async () => {
    if (!quickApplyJob) return;
    setGeneratingCL(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ai/generate-cover-letter/${quickApplyJob.id}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCoverLetter(res.data.cover_letter);
      toast.success('Cover letter generated!');
    } catch {
      toast.error('Could not generate cover letter');
    } finally { setGeneratingCL(false); }
  };

  const closeQuickApply = () => {
    setQuickApplyJob(null);
    setAppliedSuccess(null);
    setCoverLetter('');
  };

  const saveSearchAsAlert = async () => {
    if (!isAuthenticated) { navigate('/login?redirect=/jobs'); return; }
    setSavingAlert(true);
    try {
      const keywords = searchParams.get('search') ? [searchParams.get('search')] : [];
      await api.post('/alerts', {
        name: alertName,
        keywords,
        location: searchParams.get('location') || null,
        job_type: searchParams.get('job_type') || null,
        salary_min: searchParams.get('salary_min') ? parseInt(searchParams.get('salary_min')) : null,
        frequency: alertFrequency,
      });
      toast.success('Job alert created! You\'ll be notified of matching jobs.');
      setShowAlertModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create alert');
    } finally { setSavingAlert(false); }
  };

  const toggleBookmark = (job, save) => {
    setSavedJobs(prev => {
      const next = new Set(prev);
      if (save) next.add(job.id); else next.delete(job.id);
      return next;
    });
    toast.success(save ? 'Job saved' : 'Job removed from saved');
  };

  const activeFiltersCount = [
    searchParams.get('job_type'),
    searchParams.get('remote') === 'true',
    searchParams.get('salary_min'),
    searchParams.get('salary_max'),
    searchParams.get('category'),
    searchParams.get('country'),
    searchParams.get('sector')
  ].filter(Boolean).length;
  // subcategory is a refinement of category, not counted as its own filter

  const hasSearch = searchParams.get('search') || searchParams.get('location');

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <AdBlock position="jobs-top" showEmpty />

      {/* Search Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <form onSubmit={handleSearch}>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input type="text" placeholder="Job title, keywords, or company" value={search}
                  onChange={e => setSearch(e.target.value)} className="pl-12 h-12" data-testid="job-search-input" />
              </div>
              <div className="flex-1 relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input type="text" placeholder="City or postcode" value={location}
                  onChange={e => setLocation(e.target.value)} className="pl-12 h-12" data-testid="job-location-input" />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="h-12 px-4 md:hidden"
                  onClick={() => setShowFilters(!showFilters)}>
                  <Filter className="w-5 h-5" />
                </Button>
                <Button type="submit" className="h-12 px-8 bg-[#E11D48] hover:bg-[#E11D48]/90 rounded-lg"
                  data-testid="job-search-button">
                  Search
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <aside className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-72 flex-shrink-0`}>
            <div className="sticky top-24 space-y-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <SlidersHorizontal className="w-5 h-5" />Filters
                  </h3>
                  {activeFiltersCount > 0 && (
                    <button onClick={clearFilters} className="text-sm text-[#E11D48] hover:underline"
                      data-testid="clear-filters">Clear all</button>
                  )}
                </div>
                <div className="space-y-6">
                  <div className="space-y-3">
                    <Label className="font-medium">Job Type</Label>
                    <Select value={jobType || 'all'} onValueChange={v => setJobType(v === 'all' ? '' : v)}>
                      <SelectTrigger data-testid="job-type-select"><SelectValue placeholder="All types" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        <SelectItem value="full_time">Full Time</SelectItem>
                        <SelectItem value="part_time">Part Time</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="internship">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox id="remote" checked={remote} onCheckedChange={setRemote} data-testid="remote-checkbox" />
                    <Label htmlFor="remote" className="cursor-pointer">Remote jobs only</Label>
                  </div>
                  <div className="space-y-3">
                    <Label className="font-medium">Salary Range (£)</Label>
                    <div className="flex gap-2">
                      <Input type="number" placeholder="Min" value={salaryMin}
                        onChange={e => setSalaryMin(e.target.value)} data-testid="salary-min-input" />
                      <Input type="number" placeholder="Max" value={salaryMax}
                        onChange={e => setSalaryMax(e.target.value)} data-testid="salary-max-input" />
                    </div>
                  </div>
                  {categories.length > 0 && (
                    <div className="space-y-3">
                      <Label className="font-medium">Category</Label>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className="w-full justify-between font-normal" data-testid="category-dropdown-trigger">
                            <span className="truncate">
                              {categories.find(c => (c.slug || c.name) === category)?.name || 'All categories'}
                              {subcategory && ` — ${subcategory.replace(/-/g, ' ')}`}
                            </span>
                            <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56" align="start">
                          <DropdownMenuItem onClick={clearCategoryFilter}>All categories</DropdownMenuItem>
                          {categories.map(cat => {
                            const subs = subcategoriesByCategory[cat.id] || [];
                            if (subs.length === 0) {
                              return (
                                <DropdownMenuItem key={cat.id} onClick={() => selectCategory(cat)}>
                                  {cat.name}
                                </DropdownMenuItem>
                              );
                            }
                            return (
                              <DropdownMenuSub key={cat.id}>
                                <DropdownMenuSubTrigger>{cat.name}</DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  <DropdownMenuItem onClick={() => selectCategory(cat)}>All {cat.name}</DropdownMenuItem>
                                  {subs.map(sub => (
                                    <DropdownMenuItem key={sub.id} onClick={() => selectSubcategory(cat, sub)}>
                                      {sub.name}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                            );
                          })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  {countries.length > 0 && (
                    <div className="space-y-3">
                      <Label className="font-medium">Country</Label>
                      <Select value={country || 'all'} onValueChange={v => setCountry(v === 'all' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="All countries" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All countries</SelectItem>
                          {countries.map(c => (
                            <SelectItem key={c.id} value={c.code}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button onClick={handleSearch} className="w-full bg-[#003366] hover:bg-[#003366]/90"
                    data-testid="apply-filters-button">
                    Apply Filters
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Save search as alert */}
            <Card className="border-dashed border-[#003366]/30 bg-[#003366]/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Bell className="w-5 h-5 text-[#003366] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#003366]">Get job alerts</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Be notified when new jobs match your search.</p>
                    <Button size="sm" variant="outline" className="mt-3 w-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white text-xs h-8"
                      onClick={() => { if (!isAuthenticated) { navigate('/login?redirect=/jobs'); } else { setShowAlertModal(true); } }}>
                      <Bell className="w-3.5 h-3.5 mr-1.5" />
                      Save as Alert
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <AdBlock position="jobs-sidebar" showEmpty />
            </div>
          </aside>

          {/* Results */}
          <main className="flex-1 min-w-0">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {searchParams.get('search') ? `Jobs for "${searchParams.get('search')}"` : 'All Jobs'}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {totalCount.toLocaleString()} jobs found
                  {searchParams.get('location') && ` in ${searchParams.get('location')}`}
                  {searchParams.get('sector') && ` in ${searchParams.get('sector').replace(/-/g, ' ')}`}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Applied summary */}
                {isAuthenticated && isJobSeeker && Object.keys(appliedMap).length > 0 && (
                  <Link to="/my-applications">
                    <Badge className="bg-[#003366] text-white cursor-pointer hover:bg-[#003366]/90">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      {Object.keys(appliedMap).length} application{Object.keys(appliedMap).length !== 1 ? 's' : ''}
                    </Badge>
                  </Link>
                )}
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="hidden md:flex">
                    {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''} active
                  </Badge>
                )}
                {/* Save as alert — inline button */}
                <Button size="sm" variant="outline" className="hidden md:flex items-center gap-1.5 text-xs"
                  onClick={() => { if (!isAuthenticated) { navigate('/login?redirect=/jobs'); } else { setShowAlertModal(true); } }}>
                  <Bell className="w-3.5 h-3.5" />
                  {hasSearch ? 'Save as Alert' : 'Create Alert'}
                </Button>
              </div>
            </div>

            {/* Active filter chips */}
            {(searchParams.get('search') || searchParams.get('location') || searchParams.get('sector') || activeFiltersCount > 0) && (
              <div className="flex flex-wrap gap-2 mb-5">
                {['search','location'].map(k => searchParams.get(k) && (
                  <Badge key={k} variant="secondary" className="px-3 py-1">
                    {searchParams.get(k)}
                    <button className="ml-2" onClick={() => {
                      const p = new URLSearchParams(searchParams); p.delete(k); p.delete('page'); setSearchParams(p);
                      if (k === 'search') setSearch(''); else setLocation('');
                    }}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
                {searchParams.get('sector') && (
                  <Badge variant="secondary" className="px-3 py-1 capitalize">
                    {searchParams.get('sector').replace(/-/g, ' ')}
                    <button className="ml-2" onClick={() => {
                      const p = new URLSearchParams(searchParams); p.delete('sector'); p.delete('page'); setSearchParams(p);
                    }}><X className="w-3 h-3" /></button>
                  </Badge>
                )}
                {searchParams.get('remote') === 'true' && (
                  <Badge variant="secondary" className="px-3 py-1">
                    Remote
                    <button className="ml-2" onClick={() => {
                      setRemote(false); const p = new URLSearchParams(searchParams); p.delete('remote'); p.delete('page'); setSearchParams(p);
                    }}><X className="w-3 h-3" /></button>
                  </Badge>
                )}
              </div>
            )}

            {/* Job Listings */}
            {loading ? (
              <div className="space-y-4">
                {[1,2,3,4,5].map(i => (
                  <Card key={i}><CardContent className="p-6">
                    <div className="flex gap-4">
                      <Skeleton className="w-14 h-14 rounded-xl" />
                      <div className="flex-1 space-y-3">
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  </CardContent></Card>
                ))}
              </div>
            ) : jobs.length > 0 ? (
              <div className="space-y-4" data-testid="job-listings">
                {jobs.map(job => (
                  <JobCard
                    key={job.id}
                    job={job}
                    appliedStatus={appliedMap[job.id] || null}
                    onQuickApply={isAuthenticated && isJobSeeker ? openQuickApply : null}
                    onBookmark={toggleBookmark}
                    isSaved={savedJobs.has(job.id)}
                  />
                ))}
                <PaginationBar page={page} pageSize={PAGE_SIZE} total={totalCount} onPageChange={goToPage} />
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#F0F4F8] flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No jobs found</h3>
                  <p className="text-muted-foreground mb-4">Try adjusting your search criteria or filters</p>
                  <div className="flex justify-center gap-3 flex-wrap">
                    <Button variant="outline" onClick={clearFilters} data-testid="clear-search">Clear all filters</Button>
                    <Button className="bg-[#003366]" onClick={() => setShowAlertModal(true)}>
                      <Bell className="w-4 h-4 mr-2" />Get Alerts for This Search
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </div>

      {/* ===== Quick Apply Modal ===== */}
      <Dialog open={!!quickApplyJob} onOpenChange={open => { if (!open) closeQuickApply(); }}>
        <DialogContent className="max-w-lg">
          {appliedSuccess ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-5 h-5" />Application Submitted!
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-sm text-muted-foreground">
                  Your application to <strong>{appliedSuccess.title}</strong> at <strong>{appliedSuccess.company_name}</strong> has been sent.
                  You can track its progress in your Application Tracker.
                </p>
                {/* Offer to create alert */}
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-start gap-3">
                    <Bell className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-blue-800 text-sm">Want more jobs like this?</p>
                      <p className="text-xs text-blue-700 mt-0.5">Set up a job alert and get notified when similar roles are posted.</p>
                      <Button size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700 text-white text-xs h-7"
                        onClick={() => { closeQuickApply(); setShowAlertModal(true); }}>
                        <Bell className="w-3.5 h-3.5 mr-1.5" />Create Job Alert
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeQuickApply}>Close</Button>
                <Link to="/my-applications"><Button className="bg-[#003366]">Track Application</Button></Link>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Quick Apply — {quickApplyJob?.title}</DialogTitle>
                <DialogDescription>{quickApplyJob?.company_name} · {quickApplyJob?.location}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="p-3 bg-gray-50 rounded-lg border text-sm text-muted-foreground">
                  Your saved CV and profile will be attached automatically.{' '}
                  <Link to={`/jobs/${quickApplyJob?.id}`} className="text-[#003366] underline">View full job details</Link>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Cover Letter (optional)</Label>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-[#003366]"
                      onClick={generateCoverLetter} disabled={generatingCL}>
                      {generatingCL
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />Generating…</>
                        : <><Sparkles className="w-3.5 h-3.5 mr-1" />AI Generate</>
                      }
                    </Button>
                  </div>
                  <Textarea
                    rows={6}
                    placeholder="Write a cover letter, or use AI Generate above…"
                    value={coverLetter}
                    onChange={e => setCoverLetter(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeQuickApply}>Cancel</Button>
                <Button onClick={submitQuickApply} disabled={applying} className="bg-[#E11D48] hover:bg-[#E11D48]/90">
                  {applying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting…</> : 'Submit Application'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Save as Alert Modal ===== */}
      <Dialog open={showAlertModal} onOpenChange={setShowAlertModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#003366]" />Create Job Alert
            </DialogTitle>
            <DialogDescription>
              Get notified by email when new jobs matching your current search are posted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Current criteria summary */}
            <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border">
              {searchParams.get('search') && <Badge variant="secondary">{searchParams.get('search')}</Badge>}
              {searchParams.get('location') && <Badge variant="secondary"><MapPin className="w-3 h-3 mr-1" />{searchParams.get('location')}</Badge>}
              {searchParams.get('job_type') && <Badge variant="secondary">{searchParams.get('job_type').replace('_',' ')}</Badge>}
              {searchParams.get('salary_min') && <Badge variant="secondary">£{parseInt(searchParams.get('salary_min')).toLocaleString()}+</Badge>}
              {!searchParams.get('search') && !searchParams.get('location') && (
                <span className="text-sm text-muted-foreground">All jobs (no filters set)</span>
              )}
            </div>
            <div className="space-y-2">
              <Label>Alert Name</Label>
              <Input value={alertName} onChange={e => setAlertName(e.target.value)}
                placeholder="e.g. React Jobs in London" />
            </div>
            <div className="space-y-2">
              <Label>Notification Frequency</Label>
              <Select value={alertFrequency} onValueChange={setAlertFrequency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instant">Instant (as soon as posted)</SelectItem>
                  <SelectItem value="daily">Daily digest</SelectItem>
                  <SelectItem value="weekly">Weekly digest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertModal(false)}>Cancel</Button>
            <Button onClick={saveSearchAsAlert} disabled={savingAlert} className="bg-[#003366] hover:bg-[#003366]/90">
              {savingAlert ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Saving…</> : <><Bell className="w-4 h-4 mr-2" />Create Alert</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
